<template>
    <main id="travel-schedule" class="travel-schedule-wrapper">
        <div class="list-banner">
            <div class="overlay"></div>
            <h1 class="banner-title">Travel Schedule</h1>
        </div>

        <SubMenu parent-route-name="tripPlanning" />

        <div class="planning-content">
            <div class="max-width-container">
                <p v-if="error" class="error-state">{{ error }}</p>

                <div class="schedule-year-nav">
                    <Bttn
                        icon="chevron_left"
                        :icon-only="true"
                        aria-label="Previous year"
                        :disabled="year <= currentYear"
                        @click="goToYear(year - 1)"
                    />
                    <h2 class="schedule-year">{{ year }}</h2>
                    <Bttn
                        icon="chevron_right"
                        :icon-only="true"
                        aria-label="Next year"
                        @click="goToYear(year + 1)"
                    />
                </div>

                <LoadingScreen
                    v-if="rowsLoading"
                    :message="`Loading your ${year} schedule…`"
                />
                <template v-else>
                    <ul class="highlight-boxes">
                        <li>
                            <span class="highlight-box__label">days away</span>
                            <span class="highlight-box__value">{{
                                plannedDays
                            }}</span>
                        </li>
                        <li
                            v-if="hasDaysOffLimit"
                            class="highlight-box--limit"
                            :class="{ over: daysOffOver }"
                        >
                            <div class="highlight-box__row">
                                <span class="highlight-box__label"
                                    >days off</span
                                >
                            </div>
                            <div class="highlight-box__row">
                                <span class="highlight-box__value">{{
                                    plannedVacation
                                }}</span>
                                <span class="highlight-box__allowance"
                                    >/ {{ vacationAllowance }}</span
                                >
                            </div>
                        </li>
                        <li
                            v-if="hasBudgetLimit"
                            class="highlight-box--limit"
                            :class="{ over: budgetOver }"
                        >
                            <div class="highlight-box__row">
                                <span class="highlight-box__label"
                                    >travel budget</span
                                >
                            </div>
                            <div class="highlight-box__row">
                                <span class="highlight-box__value"
                                    >{{ currency
                                    }}{{ $formatMoney(plannedMoney) }}</span
                                >
                                <span class="highlight-box__allowance"
                                    >/ {{ currency
                                    }}{{ $formatMoney(moneyAllowance) }}</span
                                >
                            </div>
                        </li>
                        <li
                            class="highlight-box--settings"
                            :class="{
                                'highlight-box--ghost':
                                    !hasDaysOffLimit && !hasBudgetLimit,
                            }"
                            role="button"
                            tabindex="0"
                            @click="showModal = true"
                            @keydown.enter="showModal = true"
                        >
                            <template
                                v-if="!hasDaysOffLimit && !hasBudgetLimit"
                            >
                                <span class="highlight-box__cta"
                                    >+ Set days off & budget for
                                    {{ year }}</span
                                >
                                <span
                                    v-if="suggestedVacationDays"
                                    class="highlight-box__status"
                                >
                                    {{ suggestedVacationDays }} days off
                                    suggested
                                </span>
                            </template>
                            <span
                                v-else
                                class="material-symbols-outlined"
                                aria-label="Edit days off and budget"
                                >settings</span
                            >
                        </li>
                    </ul>
                </template>
            </div>

            <template v-if="!rowsLoading">
                <ScheduleGrid
                    :rows="rows"
                    :year="year"
                    :friends-list="friendsList"
                    :show-days-off="hasDaysOffLimit"
                    :show-budget="hasBudgetLimit"
                    :currency="currency"
                    :excluded="excludedFromAdd"
                    @remove="onRemoveRow"
                    @save-error="error = 'Some changes could not be saved.'"
                />

                <div class="max-width-container">
                    <AddScheduleRow
                        :year="year"
                        :excluded="excludedFromAdd"
                        @add="onAddRow"
                    />
                </div>
            </template>
        </div>

        <YearSettingsModal
            v-if="showModal"
            :year="year"
            :vacation-days="vacationAllowance"
            :money="moneyAllowance"
            :suggested-vacation-days="suggestedVacationDays"
            :currency="currency"
            @close="showModal = false"
            @saved="onSettingsSaved"
        />
    </main>
</template>

<script>
import YearSettingsModal from "js/components/modals/YearSettingsModal";
import ScheduleGrid from "js/components/tables/ScheduleGrid";
import AddScheduleRow from "js/components/forms/AddScheduleRow";
import LoadingScreen from "js/components/common/LoadingScreen";
import { useUserStore } from "js/stores/userStore";
import { fetch, create, del, handleResponse } from "js/lib/server.js";

export default {
    name: "TravelSchedule",
    components: {
        YearSettingsModal,
        ScheduleGrid,
        AddScheduleRow,
        LoadingScreen,
    },
    setup() {
        return { userStore: useUserStore() };
    },
    data() {
        return {
            showModal: false,
            vacationAllowance: "N/A",
            moneyAllowance: "N/A",
            suggestedVacationDays: null,
            rowsByYear: {},
            rowsLoading: false,
            error: null,
        };
    },
    computed: {
        year() {
            const fromRoute = Number(this.$route.params.year);
            return Number.isInteger(fromRoute)
                ? fromRoute
                : new Date().getFullYear();
        },
        currentYear() {
            return new Date().getFullYear();
        },
        currency() {
            return this.userStore?.user?.currency ?? "$";
        },
        rows() {
            return this.rowsByYear[this.year] || [];
        },
        friendsList() {
            const byId = new Map();
            this.rows.forEach((row) => {
                (row.participants ?? []).forEach((participant) => {
                    if (!byId.has(participant.id)) {
                        byId.set(participant.id, participant);
                    }
                });
            });
            return Array.from(byId.values());
        },
        excludedFromAdd() {
            return this.rows
                .filter((row) => row.type !== "custom")
                .map((row) => ({ type: row.type, refId: row.itemable_id }));
        },
        plannedDays() {
            return this.rows.reduce(
                (sum, row) => sum + (Number(row.planned_days) || 0),
                0
            );
        },
        plannedVacation() {
            return this.sumGridField(this.rows, "vacation_days");
        },
        plannedMoney() {
            return this.sumGridField(this.rows, "money");
        },
        hasDaysOffLimit() {
            return typeof this.vacationAllowance === "number";
        },
        hasBudgetLimit() {
            return typeof this.moneyAllowance === "number";
        },
        daysOffRemaining() {
            return this.vacationAllowance - this.plannedVacation;
        },
        daysOffOver() {
            return this.hasDaysOffLimit && this.daysOffRemaining < 0;
        },
        budgetRemaining() {
            return this.moneyAllowance - this.plannedMoney;
        },
        budgetOver() {
            return this.hasBudgetLimit && this.budgetRemaining < 0;
        },
    },
    watch: {
        year: {
            immediate: true,
            handler() {
                this.loadRows(this.year);
                this.loadYearSettings();
            },
        },
    },
    methods: {
        goToYear(year) {
            this.$router.push({ name: "travelSchedule", params: { year } });
        },
        async loadRows(year) {
            if (this.rowsByYear[year]) {
                return this.rowsByYear[year];
            }
            this.rowsLoading = year === this.year;
            try {
                const rows = await fetch(
                    `/api/travel-schedule/${year}/items`
                ).then(handleResponse);
                this.rowsByYear[year] = rows;
                return rows;
            } catch (err) {
                this.error = "Could not load your schedule.";
                console.error(err);
                return [];
            } finally {
                if (year === this.year) {
                    this.rowsLoading = false;
                }
            }
        },
        sumGridField(rows, field) {
            return rows.reduce(
                (sum, row) => sum + (Number(row.me[field]) || 0),
                0
            );
        },
        async loadYearSettings() {
            const year = this.year;
            this.vacationAllowance = "N/A";
            this.moneyAllowance = "N/A";
            this.suggestedVacationDays = null;

            let setting;
            try {
                setting = await fetch(
                    `/api/travel-schedule/${year}/settings`
                ).then(handleResponse);
            } catch (err) {
                console.error(err);
                return;
            }

            if (year !== this.year) {
                return;
            }

            this.vacationAllowance = setting.vacation_days_allowance ?? "N/A";
            this.moneyAllowance = setting.money_allowance ?? "N/A";

            if (setting.exists) {
                return;
            }
            await this.loadSuggestedVacationDays(year);
        },
        async loadSuggestedVacationDays(year) {
            let previous;
            try {
                previous = await fetch(
                    `/api/travel-schedule/${year - 1}/settings`
                ).then(handleResponse);
            } catch (err) {
                console.error(err);
                return;
            }

            const previousAllowance = previous.vacation_days_allowance;
            if (typeof previousAllowance !== "number") {
                return;
            }
            const previousRows = await this.loadRows(year - 1);
            const previousPlanned = this.sumGridField(
                previousRows,
                "vacation_days"
            );
            const previousAvailable = previousAllowance - previousPlanned;
            this.suggestedVacationDays = previousAllowance + previousAvailable;
        },
        onSettingsSaved(setting) {
            this.vacationAllowance = setting.vacation_days_allowance ?? "N/A";
            this.moneyAllowance = setting.money_allowance ?? "N/A";
        },
        async onAddRow(seed) {
            this.error = null;
            try {
                const row = await create(
                    `/api/travel-schedule/${this.year}/items`,
                    seed
                ).then(handleResponse);
                (this.rowsByYear[this.year] ??= []).push(row);
            } catch (err) {
                this.error = "Could not add that to your schedule.";
                console.error(err);
            }
        },
        async onRemoveRow(id) {
            this.error = null;
            try {
                await del(`/api/travel-schedule/${this.year}/items/${id}`);
                this.rowsByYear[this.year] = this.rows.filter(
                    (row) => row.id !== id
                );
            } catch (err) {
                this.error = "Could not remove that row.";
                console.error(err);
            }
        },
    },
};
</script>
